import { useState } from 'react';
import { Card, Form, Input, Button, message, Table, Tag, Descriptions, Empty, Spin, Typography, Divider, Popconfirm } from 'antd';
import { SearchOutlined, UserOutlined, DeleteOutlined } from '@ant-design/icons';
import axios from 'axios';

const API = import.meta.env.VITE_API_BASE || '/api';
const token = () => localStorage.getItem('ar_token');
const h = () => ({ Authorization: 'Bearer ' + token() });

const { Title, Text, Paragraph } = Typography;

export default function TestSealQuery() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [deletingKey, setDeletingKey] = useState(null);

  const handleQuery = async (values) => {
    const account = values.account?.trim();
    if (!account) {
      message.warning('请输入用户唯一识别码（account）');
      return;
    }
    const sealNo = values.sealNo?.trim() || '';
    setLoading(true);
    setData(null);
    setError(null);
    try {
      const params = { account };
      if (sealNo) params.seal_no = sealNo;
      const r = await axios.get(`${API}/customer/seal-query`, {
        headers: h(),
        params,
      });
      if (r.data?.success) {
        setData(r.data.data);
        message.success('查询成功');
      } else {
        setError({
          code: r.data?.code,
          message: r.data?.message || '查询失败',
        });
        message.error(r.data?.message || '查询失败');
      }
    } catch (e) {
      const msg = e.response?.data?.message || e.message || '请求失败';
      setError({ code: e.response?.data?.code, message: msg });
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (record) => {
    const signerId = data?.signer_id;
    const sealNo = record.sealNo;
    if (!signerId || !sealNo) {
      message.error('缺少签收人ID或印章编号');
      return;
    }
    setDeletingKey(sealNo);
    try {
      const r = await axios.delete(
        `${API}/customer/signer/${signerId}/seal/${encodeURIComponent(sealNo)}`,
        { headers: h() }
      );
      if (r.data?.success) {
        message.success('印章已删除');
        setData((prev) => ({
          ...prev,
          list: (prev.list || []).filter((s) => s.sealNo !== sealNo),
        }));
      } else {
        message.error(r.data?.message || '删除失败');
      }
    } catch (e) {
      const msg = e.response?.data?.message || e.message || '删除失败';
      message.error(msg);
    } finally {
      setDeletingKey(null);
    }
  };

  const sealColumns = [
    {
      title: '印章名称',
      dataIndex: 'sealName',
      key: 'sealName',
      render: (v) => v || <Text type="secondary">-</Text>,
    },
    {
      title: '印章编号',
      dataIndex: 'sealNo',
      key: 'sealNo',
      render: (v) => v || <Text type="secondary">-</Text>,
    },
    {
      title: '是否默认章',
      dataIndex: 'isDefault',
      key: 'isDefault',
      render: (v) => (v === 1 ? <Tag color="blue">默认</Tag> : <Tag>否</Tag>),
    },
    {
      title: '印章图片',
      dataIndex: 'sealUrl',
      key: 'sealUrl',
      render: (v) =>
        v ? (
          <img src={v} alt="印章" style={{ width: 80, height: 80, objectFit: 'contain', border: '1px solid #eee' }} />
        ) : (
          <Text type="secondary">制作中…</Text>
        ),
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) =>
        record.sealNo ? (
          <Popconfirm
            title="确定删除该印章？"
            onConfirm={() => handleDelete(record)}
            okText="删除"
            cancelText="取消"
            okButtonProps={{ danger: true, loading: deletingKey === record.sealNo }}
          >
            <Button
              type="text"
              danger
              size="small"
              icon={<DeleteOutlined />}
              loading={deletingKey === record.sealNo}
            >
              删除印章
            </Button>
          </Popconfirm>
        ) : null,
    },
  ];

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      <Title level={4}>印章查询（测试样例）</Title>
      <Paragraph type="secondary">
        调用爱签接口 <Text code>/user/getUserSeals</Text> 查询指定用户的印章信息。
      </Paragraph>

      <Card style={{ marginBottom: 16 }}>
        <Form form={form} layout="vertical" onFinish={handleQuery}>
          <Form.Item
            label="用户唯一识别码（account）"
            name="account"
            rules={[{ required: true, message: '请输入 account' }]}
          >
            <Input prefix={<UserOutlined />} placeholder="请输入用户唯一识别码" allowClear />
          </Form.Item>
          <Form.Item label="印章编号（sealNo，可选）" name="sealNo">
            <Input placeholder="不填则返回该用户全部印章列表" allowClear />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />} loading={loading} block>
              印章查询
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Spin spinning={loading}>
        {error && (
          <Card style={{ marginBottom: 16, borderColor: '#ffccc7', background: '#fff2f0' }}>
            <Text type="danger">查询失败</Text>
            {error.code && <Tag color="red" style={{ marginLeft: 8 }}>错误码: {error.code}</Tag>}
            <div style={{ marginTop: 8 }}>{error.message}</div>
          </Card>
        )}

        {data && (
          <Card title="查询结果">
            <Descriptions bordered column={1} size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="用户名称">{data.name || '-'}</Descriptions.Item>
              <Descriptions.Item label="证件号">{data.idNo || '-'}</Descriptions.Item>
              <Descriptions.Item label="手机号">{data.mobile || '-'}</Descriptions.Item>
              <Descriptions.Item label="用户类型">
                {data.userType === 1 ? '企业' : data.userType === 2 ? '个人' : data.userType}
              </Descriptions.Item>
              {data.userType === 1 && (
                <Descriptions.Item label="企业名称">{data.companyName || '-'}</Descriptions.Item>
              )}
            </Descriptions>

            <Divider orientation="left">印章列表（{data.list?.length || 0}）</Divider>
            {data.list && data.list.length > 0 ? (
              <Table
                rowKey={(r, i) => r.sealNo || i}
                columns={sealColumns}
                dataSource={data.list}
                pagination={false}
                size="small"
              />
            ) : (
              <Empty description="该用户暂无印章" />
            )}
          </Card>
        )}

        {!data && !error && !loading && (
          <Card>
            <Empty description="请输入查询条件后点击「印章查询」" />
          </Card>
        )}
      </Spin>
    </div>
  );
}
